import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { OrganizationSettingModel } from '../../models/data/organization-setting.model';
import { LocalizationConstants } from '../../constants/settings.constants';
import { OrganizationService } from '../organization.service';
import { UserSessionService } from '../user-session.service';

/**
 * The organization an org-scoped profile page is rendering, provided by that page and read by
 * everything beneath it that needs to know which organization it is dealing with.
 *
 * A profile page is reachable for any organization the viewer belongs to (e.g. "View Organization"
 * from the Active Memberships list on the User Profile), and reaching one deliberately does NOT
 * change the active organization — UserSessionService.handleOrganizationUpdated only touches
 * activeOrganization when the ids already match. So on those pages activeOrganization() is the wrong
 * organization: the viewer may be an Admin there and a plain Member of the organization actually on
 * screen (DEV-2643).
 *
 * Deliberately NOT providedIn: 'root'. Each org-scoped profile page lists it in its own
 * `providers: []`, so every directive, component and embedded view rendered beneath that page
 * resolves this instance through the element injector, while everything outside a profile page
 * injects null and keeps the activeOrganization fallback. That is what lets the ~40 permission
 * checks living in reusable components (property-charge-list, member-list, payment-history-list,
 * property-list, audit-logs, avatar) resolve against the right organization with no template
 * changes, and what makes any component added to a profile page later correct by default.
 *
 * Precedence for the id, applied by PermissionUtilities.resolveOrganizationScope:
 *   options.organizationId ?? this scope ?? activeOrganization()
 */
@Injectable()
export class ProfileOrganizationScopeService {
	private readonly organizationService = inject(OrganizationService);
	private readonly userSessionService = inject(UserSessionService);

	private readonly _organizationId = signal<number | undefined>(undefined);

	public readonly organizationId = this._organizationId.asReadonly();

	// Undefined means "a scope was declared but the page hasn't resolved it yet" — the entity fetch
	// is still in flight. Every entity a profile page renders (MemberModel, PropertyModel,
	// OrganizationModel) has a required organizationId, so there is no legitimate
	// resolved-to-undefined state to confuse this with. Callers deny org-level permissions while
	// pending rather than falling back to the active organization, so admin-only controls never
	// flash visible before we know which organization we're looking at.
	public readonly isPending = computed<boolean>(() => this._organizationId() === undefined);

	//#region Scoped organization settings
	// UserSessionService only ever holds settings for the ACTIVE organization
	// (activeOrganizationSettings, wiped by an effect on every organization switch), so surfaces on a
	// profile page that read settings — the Stripe onboarding flag gating the Pay button, the
	// currency's minor units — were reading a different organization's. Fetched here per scoped
	// organization instead, and only when it actually differs from the active one, so the common
	// same-organization case costs no extra request (DEV-2643).
	private readonly fetchedSettings = signal<OrganizationSettingModel | undefined>(undefined);
	private settingsRequestedForOrganizationId: number | undefined = undefined;

	public readonly organizationSettings = computed<OrganizationSettingModel | undefined>(() => {
		const organizationId = this._organizationId();
		if (organizationId === undefined) return undefined;

		if (organizationId === this.userSessionService.activeOrganizationId()) {
			return this.userSessionService.activeOrganizationSettings();
		}

		return this.fetchedSettings();
	});

	constructor() {
		effect(() => {
			const organizationId = this._organizationId();
			const activeOrganizationId = this.userSessionService.activeOrganizationId();

			untracked(() => {
				this.fetchedSettings.set(undefined);
				this.settingsRequestedForOrganizationId = organizationId;

				// The active organization's settings are already loaded and kept fresh by
				// UserSessionService; organizationSettings() reads them directly in that case.
				if (organizationId === undefined || organizationId === activeOrganizationId) return;

				this.organizationService.getOrganizationSettings(organizationId).then(result => {
					// Drop a response that arrives after the page moved on to another organization
					// rather than caching one organization's settings as another's.
					if (this.settingsRequestedForOrganizationId !== organizationId) return;
					this.fetchedSettings.set(result.success ? result.data : undefined);
				});
			});
		});
	}

	// The settings for the organization a surface is about: the scoped organization's when rendering
	// inside a profile page, otherwise the active organization's. Undefined while a scoped fetch is in
	// flight, so callers must fail closed on anything that should not act on an organization whose
	// settings aren't known yet.
	static resolveSettings(
		profileScope: ProfileOrganizationScopeService | null,
		userSessionService: UserSessionService
	): OrganizationSettingModel | undefined {
		return profileScope
			? profileScope.organizationSettings()
			: userSessionService.activeOrganizationSettings();
	}

	// Mirrors UserSessionService.currentMinorUnits, resolved against the scoped organization.
	static resolveMinorUnits(
		profileScope: ProfileOrganizationScopeService | null,
		userSessionService: UserSessionService
	): number {
		return ProfileOrganizationScopeService.resolveSettings(profileScope, userSessionService)?.currency?.minorUnits
			?? LocalizationConstants.DEFAULT_MINOR_UNITS();
	}
	//#endregion

	public set(organizationId: number | undefined): void {
		this._organizationId.set(organizationId);
	}

	public clear(): void {
		this._organizationId.set(undefined);
	}
}
